import { Hub } from 'aws-amplify/utils';

const STORAGE_KEY = 'adminAuthReturnUrl';
const APPLIED_KEY = 'adminAuthReturnUrlApplied';

export interface ReturnLocation {
  pathname: string;
  search: string;
  hash: string;
}

let listening = false;

// Hosted UI always returns to redirectSignIn (the site root). The path the
// user opened is kept here and applied once a session exists.
export function rememberReturnUrl(storage: Storage, loc: ReturnLocation): string | null {
  const existing = readReturnUrl(storage);
  const fromLocation = returnUrlFromLocation(loc);
  if (!fromLocation) {
    return existing;
  }
  if (existing && isPostLoginLanding(fromLocation) && !isPostLoginLanding(existing)) {
    return existing;
  }
  writeReturnUrl(storage, fromLocation);
  clearApplied(storage);
  return fromLocation;
}

export function storeAuthReturnUrl(url: string, storage: Storage): void {
  if (!isSafeReturnUrl(url) || alreadyApplied(storage)) {
    return;
  }
  const existing = readReturnUrl(storage);
  if (existing && isPostLoginLanding(url) && !isPostLoginLanding(existing)) {
    return;
  }
  writeReturnUrl(storage, url);
}

export function consumeReturnUrl(storage: Storage): string | null {
  const raw = readReturnUrl(storage);
  clearReturnUrl(storage);
  const url = raw && isSafeReturnUrl(raw) ? raw : null;
  // A customState event can arrive after the guard has already chosen a route.
  // Marking it applied stops that event from sending a later navigation backwards.
  markApplied(storage);
  return url;
}

export function clearReturnUrl(storage: Storage): void {
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // Storage can throw in locked-down browsers; the in-page session still ends.
  }
}

export function listenForAuthReturnUrl(): void {
  if (listening) {
    return;
  }
  listening = true;
  Hub.listen('auth', ({ payload }) => {
    if (payload.event !== 'customOAuthState' || typeof payload.data !== 'string') {
      return;
    }
    storeAuthReturnUrl(payload.data, sessionStorage);
  });
}

export function returnUrlFromLocation(loc: ReturnLocation): string | null {
  if (isOAuthCallback(loc.search)) {
    return null;
  }
  const url = `${loc.pathname || '/'}${loc.search || ''}${loc.hash || ''}`;
  return isSafeReturnUrl(url) ? url : null;
}

export function isSafeReturnUrl(url: string): boolean {
  if (!url || url.length > 2000) {
    return false;
  }
  if (!url.startsWith('/') || url.startsWith('//') || url.startsWith('/\\')) {
    return false;
  }
  if (url.includes('\\') || url.includes('://') || /\s/.test(url)) {
    return false;
  }
  let decoded: string;
  try {
    decoded = decodeURIComponent(url);
  } catch {
    return false;
  }
  if (!decoded.startsWith('/') || decoded.startsWith('//') || decoded.startsWith('/\\')) {
    return false;
  }
  if (decoded.includes('\\') || decoded.includes('://') || /\s/.test(decoded)) {
    return false;
  }
  return decoded !== '/';
}

function isPostLoginLanding(url: string): boolean {
  return url === '/customers' || url === '/customers/';
}

function isOAuthCallback(search: string): boolean {
  const params = new URLSearchParams(search);
  return params.has('code') && params.has('state');
}

function alreadyApplied(storage: Storage): boolean {
  try {
    return storage.getItem(APPLIED_KEY) !== null;
  } catch {
    return false;
  }
}

function markApplied(storage: Storage): void {
  try {
    storage.setItem(APPLIED_KEY, '1');
  } catch {
    // Storage can be unavailable.
  }
}

function clearApplied(storage: Storage): void {
  try {
    storage.removeItem(APPLIED_KEY);
  } catch {
    // Storage can be unavailable.
  }
}

function readReturnUrl(storage: Storage): string | null {
  try {
    return storage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeReturnUrl(storage: Storage, url: string): void {
  try {
    storage.setItem(STORAGE_KEY, url);
  } catch {
    // The OAuth customState still carries the path when storage is unavailable.
  }
}
